// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import { css } from '@linaria/core';

import { themeCssVar as globalTheme } from '../../utils/theme';

export const theme = {
  transitionDuration: '250ms',
  containerWidth: globalTheme.sizing.dimensionXxxsmall,
  containerHeight: globalTheme.sizing.dimensionXxxsmall,
  containerRadius: globalTheme.sizing.borderRadiusXsmall,
  touchTargetCoarseWidth: globalTheme.sizing.dimensionXlarge,
  touchTargetCoarseHeight: globalTheme.sizing.dimensionXlarge,
  touchTargetFineWidth: globalTheme.sizing.dimensionXxxsmall,
  touchTargetFineHeight: globalTheme.sizing.dimensionXxxsmall,
  containerBackgroundSelected: globalTheme.color.backgroundCheckboxSelectedEnabled,
  containerBackgroundDisabled: globalTheme.color.backgroundCheckboxDisabled,
  containerBorderUnselected: globalTheme.color.borderCheckboxUnselectedEnabled,
  containerBorderSelected: 'transparent',
  containerBorderDisabled: globalTheme.color.borderCheckboxUnselectedDisabled,
  containerBorderError: globalTheme.color.borderCheckboxUnselectedError,
  containerOutlineWidth: globalTheme.sizing.borderWidthRegular,
  containerOutlineOffset: globalTheme.sizing.paddingXxsmall,
  containerBorderWidth: globalTheme.sizing.borderWidthThin,
  iconWidth: globalTheme.sizing.iconXsmall,
  iconHeight: globalTheme.sizing.iconXsmall,
  iconSelected: globalTheme.color.iconCheckboxSelectedEnabled,
  iconDisabled: globalTheme.color.iconCheckboxSelectedDisabled,
  overlay: globalTheme.color.interactiveButtonPrimary,
  overlayDisabled: 'transparent',
  focusBackground: globalTheme.color.borderFocusDefault,
};

const cssVariable = {
  touchTargetWidth: `--touch-target-width`,
  touchTargetHeight: `--touch-target-height`,
  accentColor: `--tui-checkbox-accent-color`,
};

const container = css`
  position: relative;
  width: ${theme.containerWidth};
  height: ${theme.containerHeight};

  ${`${cssVariable.accentColor}: ${theme.containerBackgroundSelected};`}

  @media (pointer: fine) {
    ${cssVariable.touchTargetWidth}: ${theme.touchTargetFineWidth};
    ${cssVariable.touchTargetHeight}: ${theme.touchTargetFineHeight};
  }

  @media (pointer: coarse) {
    ${cssVariable.touchTargetWidth}: ${theme.touchTargetCoarseWidth};
    ${cssVariable.touchTargetHeight}: ${theme.touchTargetCoarseHeight};
  }

  > input[type='checkbox'] {
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    transform: translate(-50%, -50%);
    margin: 0;
    padding: 0;
    opacity: 0;
    box-sizing: border-box;

    min-width: var(${cssVariable.touchTargetWidth});
    min-height: var(${cssVariable.touchTargetHeight});
  }

  &:not([data-disabled]):not([data-readonly]) > input[type='checkbox'] {
    cursor: pointer;
  }

  border-radius: ${theme.containerRadius};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: ${theme.containerBorderWidth} solid ${theme.containerBorderUnselected};
  box-sizing: border-box;
  transition: background ${theme.transitionDuration} ease;
  outline-width: ${theme.containerOutlineWidth};
  outline-offset: ${theme.containerOutlineOffset};
  outline-color: ${theme.focusBackground};
  outline-style: none;

  &[data-disabled] {
    background: ${theme.containerBackgroundDisabled};
    border-color: ${theme.containerBorderDisabled};
  }

  &[data-readonly] {
    background: ${theme.containerBackgroundDisabled};
    border-color: ${theme.containerBorderDisabled};
  }

  &[data-checked]:not([data-disabled]):not([data-readonly]) {
    background: var(${cssVariable.accentColor});
    border-color: ${theme.containerBorderSelected};
  }

  &[data-indeterminate]:not([data-disabled]):not([data-readonly]) {
    background: ${theme.containerBackgroundSelected};
    border-color: ${theme.containerBorderSelected};
  }

  &[data-focused] {
    outline-style: solid;
  }

  &[data-error] {
    border-color: ${theme.containerBorderError};
  }
`;

const overlay = css`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: ${theme.containerRadius};
  outline: ${theme.containerBorderWidth} solid ${theme.overlay};
  opacity: 0;
  background-color: ${theme.overlay};

  .${container}[data-disabled] &,
  .${container}[data-readonly] & {
    background-color: ${theme.overlayDisabled};
  }

  @media (hover: hover) and (pointer: fine) {
    .${container}:hover:not([data-disabled]):not([data-readonly]) & {
      opacity: 1;
    }
  }

  .${container}:active:not([data-disabled]):not([data-readonly]) & {
    opacity: 1;
  }

  .${container}[data-hovered]:not([data-disabled]):not([data-readonly]) & {
    opacity: 1;
  }
`;

const icon = css`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${theme.iconWidth};
  height: ${theme.iconHeight};
  transform: translate(-50%, -50%);
  pointer-events: none;
  color: ${theme.iconSelected};
  z-index: 1;
  opacity: 0;
  transition: opacity ${theme.transitionDuration} ease;
  .${container}[data-disabled] &,
  .${container}[data-readonly] & {
    color: ${theme.iconDisabled};
  }
`;

const selectedIcon = css`
  .${container}[data-checked]:not([data-indeterminate]) & {
    opacity: 1;
  }
`;

const mixedIcon = css`
  .${container}[data-indeterminate] & {
    opacity: 1;
  }
`;

const styles = {
  container,
  overlay,
  icon,
  selectedIcon,
  mixedIcon,
};

export default styles;
